import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { workshopsSchemas } from '../lib/schemas';

/* ── Hero ── */
const HeroSection = styled.section`
  background-color: #7AA6A2;
  padding: 6rem 2rem 7rem;
  text-align: center;
  position: relative;
  z-index: 0;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 65%;
    height: 100%;
    background-image: url('/img/background.png');
    background-repeat: no-repeat;
    background-size: cover;
    -webkit-mask-image: linear-gradient(to right, transparent 0%, black 50%);
    mask-image: linear-gradient(to right, transparent 0%, black 50%);
    pointer-events: none;
    z-index: -1;
  }
`;

const HeroTitle = styled.h1`
  color: #fff;
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: 700;

  @media (max-width: 600px) {
    font-size: 2.2rem;
  }
`;

const HeroWave = styled.div`
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  line-height: 0;
  pointer-events: none;
  svg { width: 100%; height: 64px; display: block; }
`;

/* ── Lead (below hero) ── */
const LeadSection = styled.section`
  background-color: #fff;
  padding: 4rem 2rem 4.5rem;
  text-align: center;
`;

const LeadText = styled.p`
  color: #555;
  font-size: 1.2rem;
  line-height: 1.8;
  max-width: 640px;
  margin: 0 auto;
`;

/* ── Section shell ── */
const ContentSection = styled.section`
  padding: 5.5rem 2rem;
  max-width: 1000px;
  margin: 0 auto;
`;

const SectionLabel = styled.p`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: var(--accent-color);
  margin-bottom: 0.75rem;
  font-weight: 600;
  text-align: center;
`;

const SectionTitle = styled.h2`
  color: #333;
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 1rem;
  font-family: var(--font-heading);
  text-align: center;
`;

const SectionSubtitle = styled.p`
  color: #777;
  font-size: 1.05rem;
  max-width: 580px;
  margin: 0 auto 4rem;
  line-height: 1.75;
  text-align: center;
`;

/* ── Workshop cards ── */
const WorkshopsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const WorkshopCard = styled.div`
  background: #fff;
  border-radius: 16px;
  border: 1px solid #eee;
  box-shadow: 0 2px 12px rgba(0,0,0,0.04);
  overflow: hidden;
  display: grid;
  grid-template-columns: 6px 1fr;
`;

const WorkshopAccent = styled.div`
  background-color: var(--primary-color);
`;

const WorkshopBody = styled.div`
  padding: 2.25rem 2.5rem;

  @media (max-width: 600px) {
    padding: 1.75rem 1.5rem;
  }
`;

const WorkshopHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const WorkshopIcon = styled.div`
  font-size: 2rem;
  flex-shrink: 0;
`;

const WorkshopTitle = styled.h3`
  color: #333;
  font-size: 1.4rem;
  font-weight: 700;
  font-family: var(--font-heading);
  margin: 0;
  line-height: 1.3;
`;

const WorkshopIntro = styled.p`
  color: #555;
  font-size: 1.02rem;
  line-height: 1.8;
  margin-bottom: 1.5rem;
`;

const CoverageLabel = styled.p`
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #7AA6A2;
  font-weight: 700;
  margin-bottom: 0.75rem;
`;

const CoverageList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 0.5rem 2rem;
`;

const CoverageItem = styled.li`
  color: #555;
  font-size: 0.95rem;
  line-height: 1.5;
  padding-left: 1.25rem;
  position: relative;

  &::before {
    content: '→';
    position: absolute;
    left: 0;
    color: #7AA6A2;
    font-weight: 700;
  }
`;

/* ── Scripture pull quote ── */
const QuoteSection = styled.section`
  background-color: #333;
  padding: 5rem 2rem;
  text-align: center;
`;

const QuoteMark = styled.div`
  font-family: var(--font-heading);
  font-size: 5rem;
  color: var(--accent-color);
  line-height: 0.5;
  margin-bottom: 1.5rem;
  opacity: 0.8;
`;

const QuoteText = styled.blockquote`
  font-family: var(--font-heading);
  font-style: italic;
  font-size: 1.65rem;
  color: #fff;
  max-width: 700px;
  margin: 0 auto 1.25rem;
  line-height: 1.5;
  font-weight: 400;

  @media (max-width: 600px) {
    font-size: 1.25rem;
  }
`;

const QuoteSource = styled.p`
  color: var(--accent-color);
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin: 0;
`;

/* ── Booking CTA ── */
const BookingBanner = styled.section`
  background-color: #7AA6A2;
  padding: 5rem 2rem;
  text-align: center;
`;

const BookingTitle = styled.h2`
  color: #fff;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  font-family: var(--font-heading);
`;

const BookingText = styled.p`
  color: rgba(255,255,255,0.9);
  font-size: 1.1rem;
  max-width: 560px;
  margin: 0 auto 2rem;
  line-height: 1.7;
`;

const BookingButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;



const BookingButtonOutline = styled(Link)`
  display: inline-block;
  background-color: transparent;
  color: #fff;
  font-weight: 600;
  font-size: 1rem;
  padding: 0.8rem 1.8rem;
  border-radius: 100px;
  border: 2px solid rgba(255,255,255,0.7);
  text-decoration: none;
  transition: border-color 0.2s;

  &:hover {
    border-color: #fff;
    background-color: rgba(255,255,255,0.1);
    color: #fff;
  }
`;

/* ── Data ── */
const WORKSHOPS = [
  {
    icon: '🤖',
    title: 'Artificial Intelligence & The Christian Life',
    intro: 'In an age of rapid technological change, AI presents both unprecedented opportunity and challenging ethical questions. How should Christians think about it — not just as users, but as builders, leaders, and witnesses?',
    coverage: [
      'What AI actually is (and isn\'t)',
      'Theological foundations for technology',
      'Privacy, bias, and human dignity',
      'Responsible use in work and ministry',
      'The image of God and what makes us unique',
      'Preparing for continued change biblically',
      'Gospel witness in the tech industry',
      'Practical tools for leaders and teams',
    ],
  },
  {
    icon: '🏗️',
    title: 'Entrepreneurship & Biblical Calling',
    intro: 'God has wired many believers with entrepreneurial gifts — the ability to see opportunity, solve problems, create value, and build organizations. But is entrepreneurship a legitimate calling? How does building a business relate to serving God\'s kingdom?',
    coverage: [
      'The theology of work and why it matters',
      'Discovering your entrepreneurial gifts',
      'Building on biblical principles',
      'Stewarding people, resources, and opportunity',
      'Leading with integrity in competitive markets',
      'Creating culture that reflects Christ\'s character',
    ],
  },
  {
    icon: '🌍',
    title: 'Business as a Mission',
    intro: 'For too long, Christians have viewed business and mission as separate categories. But what if your company, your career, and your marketplace calling are central to God\'s mission in the world — not secondary to it?',
    coverage: [
      'Biblical foundations for Business as a Mission',
      'The Great Commission in marketplace contexts',
      'Sustainable businesses that serve and witness',
      'Models of BAM: from tentmaking to social enterprise',
      'Integrating gospel purpose into company culture',
      'Real-world case studies from Southeast Asia and beyond',
    ],
  },
  {
    icon: '💍',
    title: 'Premarital Counseling',
    intro: 'Many couples enter marriage carrying hidden struggles, unspoken fears, and unresolved patterns. This workshop helps couples build marriages on honesty, grace, and biblical covenant — laying a foundation that lasts.',
    coverage: [
      'Biblical foundations for marriage and covenant',
      'Communication patterns and conflict resolution',
      'Finances, expectations, and life goals',
      'Navigating family backgrounds and past hurts',
      'Forgiveness, grace, and spiritual leadership',
      'Building a marriage culture of ongoing openness',
    ],
  },
];

const Workshops = () => {
  return (
    <Layout
      title="Workshops"
      description="Practical, biblically-grounded workshops on AI, entrepreneurship, and business as a mission — for churches, conferences, retreats, and organizations."
      path="/workshops"
      image="/img/og-default.jpg"
      imageAlt="Workshops on AI, faith, and entrepreneurship by Building on Truth"
      keywords="Christian AI workshop, faith and technology training, business as mission workshop, church AI training"
      jsonLd={workshopsSchemas()}
    >
      <HeroSection>
        <HeroTitle>Workshops</HeroTitle>
        <HeroWave>
          <svg viewBox="0 0 1440 64" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,64 C360,0 1080,0 1440,64 L1440,64 L0,64 Z" fill="#fff" />
          </svg>
        </HeroWave>
      </HeroSection>

      <LeadSection>
        <LeadText>
          Practical, biblically-grounded teaching on the questions that matter most to Christians in business and technology. Available for churches, conferences, retreats, and organizations.
        </LeadText>
      </LeadSection>

      <ContentSection>
        <SectionLabel>What We Teach</SectionLabel>
        <SectionTitle>Four workshops, one conviction</SectionTitle>
        <SectionSubtitle>
          Faith belongs at the center of work, technology, and business — not tucked away for Sunday. These workshops give your community the theological foundations and practical tools to live that out.
        </SectionSubtitle>

        <WorkshopsGrid>
          {WORKSHOPS.map((w) => (
            <WorkshopCard key={w.title}>
              <WorkshopAccent />
              <WorkshopBody>
                <WorkshopHeader>
                  <WorkshopIcon>{w.icon}</WorkshopIcon>
                  <WorkshopTitle>{w.title}</WorkshopTitle>
                </WorkshopHeader>
                <WorkshopIntro>{w.intro}</WorkshopIntro>
                <CoverageLabel>What we cover</CoverageLabel>
                <CoverageList>
                  {w.coverage.map((item) => (
                    <CoverageItem key={item}>{item}</CoverageItem>
                  ))}
                </CoverageList>
              </WorkshopBody>
            </WorkshopCard>
          ))}
        </WorkshopsGrid>
      </ContentSection>

      <QuoteSection>
        <QuoteMark>"</QuoteMark>
        <QuoteText>
          So whether you eat or drink or whatever you do, do it all for the glory of God.
        </QuoteText>
        <QuoteSource>1 Corinthians 10:31</QuoteSource>
      </QuoteSection>

      <BookingBanner>
        <BookingTitle>Bring a workshop to your community</BookingTitle>
        <BookingText>
          We tailor each workshop to your audience — whether that's a Sunday congregation, a leadership team, a tech conference, or a university class. Get in touch and let's find the right fit.
        </BookingText>
        <BookingButtons>
          <BookingButtonOutline to="/contact">Book a Free Call First</BookingButtonOutline>
        </BookingButtons>
      </BookingBanner>
    </Layout>
  );
};

export default Workshops;
